import type React from 'react';
import { useCallback, useState, useEffect } from 'react';

import { Box, Paper, Typography, useTheme } from '@mui/material';
import { componentLogger } from '@ragu-components/logger';
import { trackAnalyticsEvent } from '@ragu-components/ord-analytics';
import { CopyToClipboardButton } from '@ragu-components/ord-components';
import { QRCodeSVG } from 'qrcode.react';
import { TextInput, SimpleForm, useNotify, SaveContextProvider, useTranslate, useGetIdentity } from 'react-admin';

import { useIngordDataProvider } from 'src/components/flowchart/hooks/useIngordDataProvider';
import { SimpleSaveToolbar } from 'src/components/forms';

import type { TOTPRequest } from '@ragu-components/ord-types';
import type { FieldValues } from 'react-hook-form';

export const TOTP = (): React.ReactElement => {
  const dataProvider = useIngordDataProvider();
  const notify = useNotify();
  const translate = useTranslate();
  const theme = useTheme();
  const { data: identity, isLoading: identityLoading } = useGetIdentity();
  const [saving, setSaving] = useState(false);

  const [totpConfig, setTotpConfig] = useState<TOTPRequest | null>(null);

  useEffect(() => {
    const fetchTotp = async (): Promise<void> => {
      try {
        const data = await dataProvider.getTotp();
        setTotpConfig(data);
        componentLogger.info('Totp fetched', { data });
      } catch (error) {
        componentLogger.error('Totp fetch failed', { error });
        notify('ord.settings.totp.setupError', { type: 'error' });
      }
    };

    void fetchTotp();
  }, [dataProvider, notify]);

  const handleSave = useCallback(
    (values: FieldValues) => {
      const otp = values.otp;
      if (typeof otp !== 'string') {
        componentLogger.debug('Invalid TOTP');
        return;
      }
      if (!totpConfig) {
        componentLogger.debug('No TOTP config');
        return;
      }
      setSaving(true);
      dataProvider
        .createTotp({ otp, ...totpConfig })
        .then(() => {
          componentLogger.info('Totp created');
          trackAnalyticsEvent('totp_enabled', { event_category: 'security' });
          setSaving(false);
          notify('ord.settings.totp.setupSuccess', { type: 'info' });
        })
        .catch(() => {
          componentLogger.error('Totp creation failed');
          trackAnalyticsEvent('totp_enable_failed', { event_category: 'security' });
          setSaving(false);
          notify('ord.settings.totp.setupError', { type: 'error' });
        });
    },
    [dataProvider, notify, totpConfig]
  );

  const saveContext = {
    save: handleSave,
    saving,
  };

  return (
    <SaveContextProvider value={saveContext}>
      {totpConfig && (
        <Paper sx={{ overflow: 'hidden' }}>
          {!identityLoading &&
            identity !== undefined &&
            'totp_enabled' in identity &&
            identity.totp_enabled === true && (
              <Typography variant='h6' sx={{ padding: theme.spacing(0, 2) }}>
                {translate('ord.settings.totp.totpAlreadySet')}
              </Typography>
            )}
          <SimpleForm onSubmit={handleSave} toolbar={<SimpleSaveToolbar />}>
            <Box sx={{ maxWidth: '100%', textAlign: 'center', mb: 2 }}>
              <Typography variant='body1'>{translate('ord.settings.totp.scanQr')}</Typography>
              <Box my={1}>
                <QRCodeSVG title={translate('ord.settings.totp.quickAuthSetupQRCode')} value={totpConfig.uri} />
              </Box>
              <Typography variant='body1'>{translate('ord.settings.totp.setupManually')}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ wordBreak: 'break-all' }}>{totpConfig?.verification_code}</Typography>
                <CopyToClipboardButton text={totpConfig?.verification_code} />
              </Box>
            </Box>
            <TextInput
              sx={{ width: '100%' }}
              source='otp'
              label={translate('ord.settings.totp.sixDigitCode')}
              disabled={saving}
              required={true}
              validate={(value) => {
                return value !== '' && value.length === 6
                  ? undefined
                  : translate('ord.settings.totp.sixDigitCodeLengthError');
              }}
            />
          </SimpleForm>
        </Paper>
      )}
    </SaveContextProvider>
  );
};
